/**
 * StockImage — the single place every Stock vehicle image is rendered.
 *
 * Phase 8.1.2/8.1.3 (image delivery): centralises the URL-agnostic LCP hints
 * so they are applied consistently:
 *   - `loading`        eager for the LCP candidate (priority), lazy otherwise
 *   - `fetchPriority`  high for the LCP candidate (React 18.3+ DOM prop)
 *   - `decoding`       async (off-main-thread decode)
 *   - `width`/`height` intrinsic dims for CLS reservation when known
 *
 * Layer 2 (responsive `srcset`/`sizes`) is intentionally a pass-through prop,
 * NOT generated here: the image-transform CDN is not yet chosen (ADR 005,
 * decisions/005-image-delivery-strategy.md) so there is no width-param scheme
 * to commit to, and appending params to source URLs (some of which are signed)
 * would break them. When the CDN lands, an adapter computes `srcSet`/`sizes`
 * and passes them in here — no change to call sites.
 *
 * Graceful degradation: if the URL is empty or the image fails to load (a
 * withdrawn / un-synced object 404s, the source-url fallback for an un-cached
 * slot is the vendor's IP-restricted host, etc.) we render a neutral "画像なし"
 * placeholder instead of the browser's broken-image glyph. The failure is
 * tracked per-`src`, so swapping `src` (e.g. the detail gallery) re-attempts
 * the new image rather than staying stuck on the placeholder.
 *
 * Spec: docs/stock-core-portal-architecture/findings/image-delivery-global-low-latency.md (Layers 2-3)
 */
import { useState, type CSSProperties } from 'react';

export interface StockImageProps {
  /** Image URL (passed through verbatim — never mutated here). */
  src: string;
  alt?: string;
  /**
   * Mark this image as the Largest Contentful Paint candidate (e.g. the detail
   * page hero). Loads eagerly with fetchPriority="high". Use for at most one
   * image per view; everything else stays lazy.
   */
  priority?: boolean;
  /** Intrinsic dimensions for CLS reservation, when known. */
  width?: number;
  height?: number;
  /** Layer 2 (CDN) — responsive candidates. Undefined until ADR 005 lands. */
  srcSet?: string;
  /** Only meaningful alongside `srcSet`. */
  sizes?: string;
  className?: string;
  style?: CSSProperties;
}

export function StockImage({
  src,
  alt = '',
  priority = false,
  width,
  height,
  srcSet,
  sizes,
  className,
  style,
}: StockImageProps): JSX.Element {
  // Track the failed URL (not a boolean) so a later `src` change re-attempts the
  // new image instead of staying on the placeholder.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showFallback = !src || failedSrc === src;

  if (showFallback) {
    return (
      <div
        data-testid="stock-image-fallback"
        role="img"
        aria-label={alt || 'No image'}
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f4f4f4',
          color: '#999',
          fontSize: '0.85rem',
          width,
          height,
          ...style,
        }}
      >
        No image
      </div>
    );
  }

  // Pass `fetchpriority` as the lowercase DOM attribute (via spread) so it lands
  // without React 18.x's "spell it lowercase" warning for the camelCase prop;
  // React 19 accepts the camelCase form, the lowercase attribute works on both.
  const lcpAttr = priority ? { fetchpriority: 'high' } : {};
  return (
    <img
      {...lcpAttr}
      src={src}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      width={width}
      height={height}
      className={className}
      style={style}
      onError={() => setFailedSrc(src)}
    />
  );
}
